'use client'

import { motion } from 'framer-motion'
import { Upload, CheckCircle2, Building2, Compass, Trophy, ArrowRight, Clock, Zap, Shield, PhoneCall } from 'lucide-react'
import Link from 'next/link'
import processData from '@/data/process.json'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { WhatsAppIcon } from './WhatsAppIcon'

const iconComponents = {
  Upload,
  CheckCircle2,
  Building2,
  Compass,
  Trophy,
}

const stepColors = [
  { bg: '#EFF6FF', icon: '#1D4ED8', iconBg: '#DBEAFE', border: '#BFDBFE', num: '#1D4ED8' },
  { bg: '#FFFBEB', icon: '#D97706', iconBg: '#FEF3C7', border: '#FDE68A', num: '#D97706' },
  { bg: '#F0FDF4', icon: '#059669', iconBg: '#D1FAE5', border: '#A7F3D0', num: '#059669' },
  { bg: '#FAF5FF', icon: '#7C3AED', iconBg: '#EDE9FE', border: '#DDD6FE', num: '#7C3AED' },
  { bg: '#FFF1F2', icon: '#E11D48', iconBg: '#FFE4E6', border: '#FECDD3', num: '#E11D48' },
]

const timeEstimates = [
  '1 Business Day',
  '1-2 Business Days',
  '3-7 Business Days',
  'Real-time',
  '1 Business Day',
]

const preparationTips = [
  { icon: Shield, tip: 'Have all original documents ready and legible' },
  { icon: Zap, tip: 'Urgent processing available for time-sensitive cases' },
  { icon: Clock, tip: 'Most services completed within 3-7 business days' },
  { icon: PhoneCall, tip: 'Dedicated case manager assigned to your case' },
]

export function ProcessSection() {
  const { ref, isVisible } = useScrollAnimation()

  return (
    <section id="process" ref={ref} className="py-20 md:py-28 bg-[#F8FBFF]">
      <div className="container-narrow">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="section-badge">
            <ArrowRight className="w-4 h-4" />
            How It Works
          </span>
          <h2 className="section-title">Our 5-Step Process</h2>
          <p className="section-subtitle max-w-2xl mx-auto">
            We've refined our process over 10 years to make document clearing as smooth and stress-free as possible.
            Transparent, fast, and fully compliant.
          </p>
        </motion.div>

        {/* Quick Info Strip */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
        >
          {preparationTips.map((tip, idx) => {
            const Icon = tip.icon
            return (
              <div
                key={idx}
                className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#DBEAFE]"
                style={{ boxShadow: '0 2px 8px rgba(29,78,216,0.06)' }}
              >
                <div className="w-9 h-9 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <p className="text-[#475569] text-xs font-medium leading-relaxed">{tip.tip}</p>
              </div>
            )
          })}
        </motion.div>

        {/* Steps */}
        <div className="space-y-6 mb-16">
          {processData.process.map((item, idx) => {
            const IconComponent = iconComponents[item.icon as keyof typeof iconComponents]
            const colors = stepColors[idx]
            const isEven = idx % 2 === 0

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, x: isEven ? -40 : 40 }}
                animate={isVisible ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className={`flex flex-col md:flex-row items-start md:items-center gap-6 ${!isEven ? 'md:flex-row-reverse' : ''}`}
              >
                {/* Step Card */}
                <div
                  className="flex-1 rounded-2xl p-6 md:p-8 border transition-all duration-300 hover:-translate-y-1"
                  style={{
                    background: colors.bg,
                    borderColor: colors.border,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center w-14 h-14 rounded-2xl flex-shrink-0"
                      style={{ background: colors.iconBg, color: colors.icon }}
                    >
                      {IconComponent && <IconComponent className="w-7 h-7" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-full bg-white"
                          style={{ color: colors.num, border: `1px solid ${colors.border}` }}
                        >
                          Step {idx + 1}
                        </span>
                        <span className="text-xs font-semibold text-[#94A3B8] flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeEstimates[idx]}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#1E293B] mb-2">{item.step}</h3>
                      <p className="text-[#475569] mb-2 leading-relaxed">{item.description}</p>
                      <p className="text-sm font-semibold" style={{ color: colors.icon }}>{item.details}</p>
                    </div>
                  </div>
                </div>

                {/* Connector */}
                <div className="hidden md:flex flex-col items-center flex-shrink-0 w-16">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-medium"
                    style={{ background: colors.icon }}
                  >
                    {idx + 1}
                  </div>
                  {idx < processData.process.length - 1 && (
                    <div
                      className="w-0.5 h-8 mt-2"
                      style={{ background: `linear-gradient(to bottom, ${colors.icon}, ${stepColors[idx + 1].icon})` }}
                    />
                  )}
                </div>

                {/* Empty space for alternating layout */}
                <div className="hidden md:block flex-1" />
              </motion.div>
            )
          })}
        </div>

        {/* Completion Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isVisible ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex justify-center mb-16"
        >
          <div
            className="inline-flex items-center gap-4 px-8 py-4 rounded-2xl text-white font-semibold"
            style={{ background: 'linear-gradient(135deg, #059669, #10B981)', boxShadow: '0 8px 20px rgba(5,150,105,0.3)' }}
          >
            <Trophy className="w-6 h-6" />
            <span>Your documents are delivered successfully!</span>
            <CheckCircle2 className="w-6 h-6" />
          </div>
        </motion.div>

        {/* What to Prepare */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="rounded-3xl border border-[#DBEAFE] overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%)' }}
        >
          <div className="p-8 md:p-10">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-[#1E293B] mb-2">What to Prepare</h3>
              <p className="text-[#64748B]">Have these documents ready to speed up your application</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  title: 'Personal Documents',
                  items: ['Valid Passport Copy', 'Emirates ID (if applicable)', 'Visa Copy', 'Recent Passport Photos'],
                  color: '#1D4ED8',
                  bg: 'white',
                },
                {
                  title: 'Business Documents',
                  items: ['Trade License (if applicable)', 'MOA / AOA', 'Company Stamp', 'Authorized Signatory Letter'],
                  color: '#D97706',
                  bg: 'white',
                },
                {
                  title: 'Supporting Documents',
                  items: ['Original Certificates', 'Bank Statements (if required)', 'NOC Letters (if applicable)', 'Previous Correspondence'],
                  color: '#059669',
                  bg: 'white',
                },
              ].map((cat, idx) => (
                <div key={idx} className="rounded-xl p-5" style={{ background: cat.bg, border: '1px solid #DBEAFE' }}>
                  <h4 className="font-bold text-sm mb-3" style={{ color: cat.color }}>{cat.title}</h4>
                  <ul className="space-y-2">
                    {cat.items.map((doc, i) => (
                      <li key={i} className="flex items-center gap-2 text-[#475569] text-sm">
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: cat.color }} />
                        {doc}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#1D4ED8] text-white rounded-xl font-bold hover:bg-[#1E40AF] transition-all duration-300 shadow-soft hover:-translate-y-0.5"
              >
                Start Your Application
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="https://wa.me/971586155899"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#1D4ED8] text-[#1D4ED8] rounded-xl font-bold hover:bg-[#EFF6FF] transition-all duration-300"
              >
                <WhatsAppIcon className="w-5 h-5 fill-current" />
                Ask via WhatsApp
              </a>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  )
}
